import React, { useState } from 'react';

export type MessageIdentityKind = 'ai' | 'user';

interface MessageIdentityLabelProps {
  className?: string;
  interactive?: boolean;
  kind?: MessageIdentityKind;
  name: string;
  style?: React.CSSProperties;
}

// Kept together so the static and interactive render paths share identical
// icon/name semantics.
// eslint-disable-next-line max-lines-per-function
export const MessageIdentityLabel: React.FC<MessageIdentityLabelProps> = ({
  className = '',
  interactive = true,
  kind = 'ai',
  name,
  style,
}) => {
  const [expanded, setExpanded] = useState(false);
  const classes = [
    className,
    'message-identity-label',
    `message-identity-label--${kind}`,
    !interactive && 'message-identity-label--static',
  ].filter(Boolean).join(' ');
  const contents = (
    <>
      <i
        className={`fa-sharp fa-thin ${kind === 'user' ? 'fa-user' : 'fa-robot'} message-identity-label__icon`}
        aria-hidden="true"
      />
      <span className="message-identity-label__name" aria-hidden="true">{name}</span>
    </>
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setExpanded(false);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.stopPropagation();
    }
  };

  if (!interactive) {
    return (
      <span
        className={classes}
        style={style}
        role="img"
        aria-label={`Sender: ${name}`}
      >
        {contents}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={classes}
      style={style}
      aria-label={`Sender: ${name}`}
      data-expanded={expanded}
      onBlur={() => setExpanded(false)}
      onClick={(event) => {
        event.stopPropagation();
        setExpanded((visible) => !visible);
      }}
      onKeyDown={handleKeyDown}
    >
      {contents}
    </button>
  );
};

export default MessageIdentityLabel;
